import { useAccounts } from "library/accounts";
import { putPaymentNotificationPreferences } from "library/api";
import { useProducts } from "library/hooks/useProducts";
import { convertObjectKeysCase, snakeToCamelCase } from "library/transforms";
import { NotificationPreferencesMutationKey } from "pages/Notifications/types";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getNotificationPreferences,
  getNotificationsAccountData,
} from "./utils";

export function useNotificationAccountDetails() {
  const products = useProducts();
  const accounts = useAccounts({
    fullCreditCardsInfo: true,
    // even though PLs are not supported, we need to fetch the PL to get its account number for display
    fullPersonalLoansInfo: true,
  });

  const parsedAccounts = [
    ...(accounts.data?.credit_cards ?? []),
    ...(accounts.data?.personal_loans ?? []),
  ].filter((account) => account.status !== "CLOSED");

  const transformedAccounts = getNotificationsAccountData(
    parsedAccounts,
    products.data,
  );

  return {
    accountsLoading: accounts.isLoading || products.isLoading,
    accountsError: accounts.isError || products.isError,
    transformedAccounts,
  };
}

export function useNotificationPreferences(token: string) {
  const [switchLoading, setSwitchLoading] = useState<
    NotificationPreferencesMutationKey | undefined
  >(undefined);
  const [switchError, setSwitchError] = useState<
    NotificationPreferencesMutationKey | undefined
  >(undefined);
  const queryClient = useQueryClient();

  function showError(errorKey: NotificationPreferencesMutationKey) {
    setSwitchError(errorKey);
    setSwitchLoading(undefined);
  }

  const getPreferences = useQuery(
    ["notificationDetails", token],
    getNotificationPreferences(token),
  );

  const updatePreferences = useMutation(putPaymentNotificationPreferences, {
    onMutate: () => {
      // Reset error
      setSwitchError(undefined);
    },
    onSuccess: (response, variables) => {
      try {
        const newPreferences = convertObjectKeysCase(
          response.data,
          snakeToCamelCase,
        );
        queryClient.setQueryData(["notificationDetails", variables.token], {
          personalDetails: getPreferences.data?.personalDetails,
          preferences: {
            ...getPreferences.data?.preferences,
            ...newPreferences,
          },
          token: getPreferences.data?.token,
        });
      } catch (error) {
        const errorKey = Object.keys(variables.payload)[0];
        setSwitchError(errorKey as NotificationPreferencesMutationKey);
      }
      setSwitchLoading(undefined);
      // Todo: Handle success notification
    },
    onError: (_, variables) => {
      const errorKey = Object.keys(variables.payload)[0];
      showError(errorKey as NotificationPreferencesMutationKey);
    },
  });
  return {
    getPreferences,
    switchError,
    switchLoading,
    setSwitchLoading,
    updatePreferences,
  };
}
